/**
 * Adapters for the shared <PromptEditor scope> component (PR-E).
 *
 * Each adapter implements `PromptEditorAdapter` from `./PromptEditor.types`
 * — its job is to smooth over the scope-conditional data hooks, save
 * payload shapes, affordance flags, and copy strings so the shared body
 * sees ONE normalized contract regardless of scope.
 *
 * See PromptEditor.tsx for the canonical scope-deviation table.
 */
import { MessageSquare, FileType } from 'lucide-react';
import {
  usePromptTemplates,
  useCreatePromptTemplate,
  useUpdatePromptTemplate,
  useDeletePromptTemplate,
  useClonePromptFromSystem,
  type PromptTemplate,
} from '@/lib/use-prompt-templates';
import {
  useSysadminPrompts,
  useCreateSysadminPrompt,
  useUpdateSysadminPrompt,
  useToggleSysadminPromptVisibility,
  useDeleteSysadminPrompt,
  type SysadminPrompt,
} from '@/lib/use-sysadmin-prompts';
import {
  EMPTY_DRAFT,
  EMPTY_SYSADMIN_DRAFT,
  buildTenantCreateBody,
  buildTenantUpdateBody,
  buildSysadminCreateBody,
  buildSysadminUpdateBody,
} from './PromptEditor.helpers';
import type {
  ListResult,
  MutationFn,
  NormalizedPrompt,
  PromptDraft,
  PromptEditorAdapter,
  PromptEditorScope,
  SavePromptArgs,
  ToggleVisibilityFn,
} from './PromptEditor.types';
import type { PromptVariableEntry } from '@/components/studio';

// ─── Row normalisers ────────────────────────────────────────────────────────

function normalizeTenantRow(p: PromptTemplate): NormalizedPrompt {
  return {
    id: p.id,
    command: p.command,
    title: p.title,
    description: p.description,
    content: p.content,
    variables: p.variables as PromptVariableEntry[],
    category: p.category,
    isSystem: p.isSystem,
    clonedFromSystemId: p.clonedFromSystemId,
    visibleToTenant: false, // tenant adapter never surfaces this
    updatedAt: null,
  };
}

function normalizeSysadminRow(p: SysadminPrompt): NormalizedPrompt {
  return {
    id: p.id,
    command: p.command,
    title: p.title,
    description: p.description,
    content: p.content,
    variables: p.variables as PromptVariableEntry[],
    category: p.category,
    isSystem: false, // sysadmin owns every row it sees
    clonedFromSystemId: null,
    visibleToTenant: p.visibleToTenant,
    updatedAt: p.updatedAt,
  };
}

// ─── Tenant adapter ─────────────────────────────────────────────────────────

export const tenantAdapter: PromptEditorAdapter = Object.freeze({
  // ─── Page chrome ─────────────────────────────────────────────────────
  pageTitle: 'Prompts',
  pageSubtitle:
    'Slash-command templates with typed variables. Type /command in chat to invoke. System prompts authored by your platform administrator appear here as read-only with the System badge.',
  newSheetTitle: 'New prompt',
  sheetDescription:
    'Define a slash-command template. Reference variables in the content with {{name}}.',
  emptyStateMessage: 'No prompts yet. Create your first prompt with the button above.',
  deleteConfirmMessage:
    "Anyone in this workspace using the command will get an error until it's recreated.",

  // ─── Affordance flags (Bucket B) ─────────────────────────────────────
  showCardHeader: false,
  showScopeChips: true,
  showCategoryDropdown: true,
  showSourceColumn: true,
  showVisibleToTenantColumn: false,
  showUpdatedColumn: false,
  showSystemRowActions: true,
  showPreviewPane: true,
  showHelperPanel: true,
  showVisibleToTenantSwitch: false,
  showProvenanceBanner: true,
  showVariableEditor: true,
  showRowCount: true,

  // ─── Layout flags (promoted from scope === checks, PR-E audit L-2) ───
  newButtonPlacement: 'header',
  HeadingIcon: MessageSquare,

  // ─── Draft factories (promoted from scope === checks, PR-E audit L-2)
  emptyDraft(): PromptDraft {
    return { ...EMPTY_DRAFT };
  },
  draftFromRow(p: NormalizedPrompt): PromptDraft {
    return {
      id: p.id,
      command: p.command,
      title: p.title,
      description: p.description ?? '',
      content: p.content,
      variables: p.variables,
      category: p.category ?? 'general',
      clonedFromSystemId: p.clonedFromSystemId,
      // Tenant never carries visibleToTenant on the draft.
      visibleToTenant: undefined,
    };
  },

  // ─── Placeholder strings ─────────────────────────────────────────────
  commandPlaceholder: 'my-prompt',
  titlePlaceholder: 'My Prompt',
  descriptionPlaceholder: 'What this prompt does.',

  // ─── Data hooks ──────────────────────────────────────────────────────
  useList(): ListResult {
    const { data, isLoading, error } = usePromptTemplates();
    return {
      prompts: (data?.data ?? []).map(normalizeTenantRow),
      loading: isLoading,
      error: error ?? null,
    };
  },

  // ─── Save (create OR update) ─────────────────────────────────────────
  useSave() {
    const createPrompt = useCreatePromptTemplate();
    const updatePrompt = useUpdatePromptTemplate();
    return {
      saveInFlight: createPrompt.isPending || updatePrompt.isPending,
      save({ body, isEdit, id, onSuccess, onError }: SavePromptArgs) {
        if (isEdit) {
          if (!id) {
            onError(new Error('Internal: update called without id'));
            return;
          }
          updatePrompt.mutate(
            { id, ...(body as Record<string, unknown>) } as Parameters<
              typeof updatePrompt.mutate
            >[0],
            {
              onSuccess: () => onSuccess(),
              onError,
            },
          );
        } else {
          createPrompt.mutate(
            body as Parameters<typeof createPrompt.mutate>[0],
            {
              onSuccess: () => onSuccess(),
              onError,
            },
          );
        }
      },
    };
  },

  useDelete(): MutationFn {
    const del = useDeletePromptTemplate();
    return {
      inFlight: del.isPending,
      run({ id, onSuccess, onError }) {
        del.mutate({ id }, { onSuccess: () => onSuccess(), onError });
      },
    };
  },

  useToggleVisibility(): ToggleVisibilityFn | null {
    // Tenant adapter never toggles visibility — sysadmin owns the
    // visibility surface. Returning null instead of a stub makes the
    // contract self-documenting: the shared body checks `!== null`
    // before wiring the Switch into the row.
    return null;
  },

  useCloneFromSystem(): MutationFn | null {
    const clone = useClonePromptFromSystem();
    return {
      inFlight: clone.isPending,
      run({ id, onSuccess, onError }) {
        clone.mutate({ systemId: id }, { onSuccess: (resp) => onSuccess(resp), onError });
      },
    };
  },

  // ─── Payload serializers (Bucket C) ──────────────────────────────────
  serializeCreatePayload(draft: PromptDraft) {
    return buildTenantCreateBody(draft);
  },
  serializeUpdatePayload(draft: PromptDraft) {
    return buildTenantUpdateBody(draft);
  },
});

// ─── Sysadmin adapter ───────────────────────────────────────────────────────

export const sysadminAdapter: PromptEditorAdapter = Object.freeze({
  // ─── Page chrome ─────────────────────────────────────────────────────
  pageTitle: 'Prompt Templates',
  pageSubtitle:
    "Sysadmin-authored prompt templates. Toggle \"Visible to tenant\" to surface a prompt in every tenant's Studio Prompts tab as a read-only \"System\" entry.",
  newSheetTitle: 'New prompt',
  sheetDescription:
    "Sysadmin-authored prompt template. Toggle \"Visible to tenant\" to publish into every tenant's Studio Prompts tab.",
  emptyStateMessage: 'No prompts yet. Create your first prompt with the button above.',
  deleteConfirmMessage:
    "All tenants currently using this system prompt will get an error until it's recreated.",

  // ─── Affordance flags (Bucket B) ─────────────────────────────────────
  showCardHeader: true,
  showScopeChips: false,
  showCategoryDropdown: false,
  showSourceColumn: false,
  showVisibleToTenantColumn: true,
  showUpdatedColumn: true,
  showSystemRowActions: false,
  showPreviewPane: false,
  showHelperPanel: false,
  showVisibleToTenantSwitch: true,
  showProvenanceBanner: false,
  showVariableEditor: false,
  showRowCount: false,

  // ─── Layout flags (promoted from scope === checks, PR-E audit L-2) ───
  newButtonPlacement: 'search-row',
  HeadingIcon: FileType,

  // ─── Draft factories (promoted from scope === checks, PR-E audit L-2)
  emptyDraft(): PromptDraft {
    return { ...EMPTY_SYSADMIN_DRAFT };
  },
  draftFromRow(p: NormalizedPrompt): PromptDraft {
    return {
      id: p.id,
      command: p.command,
      title: p.title,
      description: p.description ?? '',
      content: p.content,
      variables: p.variables,
      category: p.category ?? 'general',
      clonedFromSystemId: p.clonedFromSystemId,
      // Sysadmin carries the current row's visibility through so the
      // in-sheet Switch reflects state.
      visibleToTenant: p.visibleToTenant,
    };
  },

  // ─── Placeholder strings ─────────────────────────────────────────────
  commandPlaceholder: 'shift-handover',
  titlePlaceholder: 'Shift Handover Report',
  descriptionPlaceholder: 'What this system prompt does.',

  // ─── Data hooks ──────────────────────────────────────────────────────
  useList(): ListResult {
    const { data, isLoading, error } = useSysadminPrompts();
    return {
      prompts: (data?.items ?? []).map(normalizeSysadminRow),
      loading: isLoading,
      error: error ?? null,
    };
  },

  // ─── Save (create OR update) ─────────────────────────────────────────
  useSave() {
    const create = useCreateSysadminPrompt();
    const update = useUpdateSysadminPrompt();
    return {
      saveInFlight: create.isPending || update.isPending,
      save({ body, isEdit, id, onSuccess, onError }: SavePromptArgs) {
        if (isEdit) {
          if (!id) {
            onError(new Error('Internal: update called without id'));
            return;
          }
          update.mutate(
            { id, ...(body as Record<string, unknown>) } as Parameters<
              typeof update.mutate
            >[0],
            {
              onSuccess: () => onSuccess(),
              onError,
            },
          );
        } else {
          create.mutate(
            body as Parameters<typeof create.mutate>[0],
            {
              onSuccess: () => onSuccess(),
              onError,
            },
          );
        }
      },
    };
  },

  useDelete(): MutationFn {
    const del = useDeleteSysadminPrompt();
    return {
      inFlight: del.isPending,
      run({ id, onSuccess, onError }) {
        del.mutate({ id }, { onSuccess: () => onSuccess(), onError });
      },
    };
  },

  useToggleVisibility(): ToggleVisibilityFn | null {
    const toggle = useToggleSysadminPromptVisibility();
    return {
      inFlight: toggle.isPending,
      run({ id, visibleToTenant, onSuccess, onError }) {
        toggle.mutate(
          { id, visibleToTenant },
          { onSuccess: () => onSuccess(), onError },
        );
      },
    };
  },

  useCloneFromSystem(): MutationFn | null {
    return null;
  },

  // ─── Payload serializers (Bucket C) ──────────────────────────────────
  serializeCreatePayload(draft: PromptDraft) {
    return buildSysadminCreateBody(draft);
  },
  serializeUpdatePayload(draft: PromptDraft) {
    return buildSysadminUpdateBody(draft);
  },
});

// ─── Adapter selector ───────────────────────────────────────────────────────

export function getAdapter(scope: PromptEditorScope): PromptEditorAdapter {
  return scope === 'sysadmin' ? sysadminAdapter : tenantAdapter;
}
